/* eslint-disable no-param-reassign */
import type { LngLat, Map } from 'maplibre-gl';

import { countyFillLayer } from '../../mapbox-layers/county/county';
import { parcelFillLayer } from '../../mapbox-layers/parcels/parcels';
import { stateFillLayer } from '../../mapbox-layers/states';
import { round } from '../common';
import { LayerIdEnums } from '../convertActiveLayer';
import Logger from '../logger';

export const EXCLUDE_DYNAMIC_URL_LAYERS = [
  LayerIdEnums.INSIGHTS_CATALOG,
  LayerIdEnums.LAYERS,
  LayerIdEnums.BOUNDARIES,
  LayerIdEnums.DEED_PLOTTER,
];

/**
 * The purpose of this function is to update the URL of Acres when the user is zooming around under different conditions
 *
 * This function does the following:
 * - Map queries the state, county, and parcel layers to get the feature that is in the middle of the screen and set the state/county what is that feature if present
 * - If the url is `/state/county/id` then we DO NOT update state or county dynamically
 *
 */
const dynamicURl = (
  page: string,
  centerPoint: LngLat,
  zoom: number,
  originalPathname: string,
  params: any,
  originalSearchParams,
  map: Map
) => {
  try {
    const { lat, lng } = centerPoint;
    let pathname = originalPathname;
    const searchParams = new URLSearchParams(originalSearchParams);

    const center = map.getCenter();

    const point = map.project(center);

    const layers: string[] = [];
    if (map.getLayer(stateFillLayer.id)) {
      layers.push(stateFillLayer.id);
    }
    if (map.getLayer(countyFillLayer.id)) {
      layers.push(countyFillLayer.id);
    }
    if (map.getLayer(parcelFillLayer.id)) {
      layers.push(parcelFillLayer.id);
    }

    // if a user zooms very fast state/county may update and be wrong
    // fix if parcels had county and state
    // currently parcels have state, but no county :(
    // if we added county to parcels then would be less network request for each parcel +1
    const data = map.queryRenderedFeatures(point, {
      layers,
    });

    const firstFeature = data[0];

    if (firstFeature && firstFeature.properties) {
      // county, county subdivision, or parcel feature
      if (firstFeature.properties.county_state_slug) {
        const state = (
          firstFeature.properties.state ?? firstFeature.properties.STUSPS
        ).toLowerCase();
        const county = firstFeature.properties.county_state_slug;

        pathname = `/plat-map/${page}/${state.toLowerCase()}/${county.toLowerCase()}`;
      }
      // state feature
      else if ('stusps' in firstFeature.properties) {
        const state = firstFeature.properties.stusps.toLowerCase();
        pathname = `/plat-map/${page}/${state.toLowerCase()}`;
      }
    }

    // If zoom hasn't changed, then return out
    if (
      round(lat, 7) === Number(searchParams?.get('lat')) &&
      round(lng, 7) === Number(searchParams?.get('lng'))
    )
      return;

    // Update the lat, lng and zoom
    searchParams.set('lat', round(lat, 7).toString());
    searchParams.set('lng', round(lng, 7).toString());
    searchParams.set('zoom', round(zoom, 2).toString());

    // If this is a /state/county/id page, then DO NOT dynamic URL
    const all = params?.all;
    if (all?.length === 3) {
      return;
    }

    // makes sure map isn't moving quickly then replaces router
    if (!map.isMoving()) {
      setTimeout(() => {
        if (!map.isMoving()) {
          const newUrl = `${pathname}?${searchParams.toString()}`;
          window.history.replaceState(
            { ...window.history.state, as: newUrl, url: newUrl },
            '',
            newUrl
          );
        }
      }, 300);
    }
  } catch (err) {
    Logger.error(err);
  }
};

export default dynamicURl;
